import { useCallback, useRef } from "react";
import { TransformControls } from "../components/UI/TransformControls.js";
import {
  calculateCornerScale,
  calculateSideScale,
  hasTransformChanged,
  screenToWorld,
  createInitialTransform,
  createFinalTransform,
  createResizeState,
  applyTransform,
} from "../utils/transformUtils.js";

export const useTransform = (viewport, updateElement, transformElement) => {
  const currentTransform = useRef(null);
  const resizeState = useRef(null);
  const initialTransform = useRef(null);
  const globalHandlers = useRef({ moveHandler: null, upHandler: null });

  // Performance optimization: throttle updates
  const lastUpdateTime = useRef(0);
  const animationFrameId = useRef(null);

  /**
   * Handle mouse movement during transform operations
   * Uses requestAnimationFrame for smooth 60fps updates
   */
  const handleGlobalMouseMove = useCallback(
    (event) => {
      if (!resizeState.current?.isResizing) return;

      // Cancel any pending animation frame
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      // Use requestAnimationFrame for smooth updates
      animationFrameId.current = requestAnimationFrame(() => {
        if (!resizeState.current?.isResizing) return;

        const { target, container, startPoint, handleType, handlePosition } =
          resizeState.current;

        // Get current mouse position in screen space
        const currentScreenPoint = {
          x: event.clientX,
          y: event.clientY,
        };

        if (handleType === "rotation") {
          handleRotation(currentScreenPoint);
        } else if (handleType === "corner") {
          handleCornerResize(
            currentScreenPoint,
            startPoint,
            handlePosition,
            target
          );
        } else if (handleType === "side") {
          handleSideResize(
            currentScreenPoint,
            startPoint,
            handlePosition,
            target
          );
        }

        // Update transform controls (this is lightweight)
        if (currentTransform.current?.update) {
          currentTransform.current.update();
        }
      });
    },
    [viewport]
  );

  /**
   * Handle rotation transformation
   */
  const handleRotation = useCallback(
    (currentScreenPoint) => {
      const { target, startPoint, startRotation } = resizeState.current;
      if (!startPoint || startRotation === undefined) {
        console.warn("Missing startPoint or startRotation");
        return;
      }

      // Convert both points from screen to world coordinates
      const canvas = viewport?.options?.events?.domElement || document.querySelector('canvas');
      const rect = canvas?.getBoundingClientRect();

      if (!rect) return;

      const startWorld = screenToWorld(
        viewport,
        startPoint.x - rect.left,
        startPoint.y - rect.top
      );
      const currentWorld = screenToWorld(
        viewport,
        currentScreenPoint.x - rect.left,
        currentScreenPoint.y - rect.top
      );

      // Rotation center is the target's position (already in world coordinates)
      const centerWorld = { x: target.x, y: target.y };

      // Calculate angles from center to mouse positions
      const startAngle = Math.atan2(
        startWorld.y - centerWorld.y,
        startWorld.x - centerWorld.x
      );
      const currentAngle = Math.atan2(
        currentWorld.y - centerWorld.y,
        currentWorld.x - centerWorld.x
      );

      // Calculate delta and normalize
      let deltaAngle = currentAngle - startAngle;
      while (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
      while (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

      // Apply rotation relative to starting rotation
      const newRotation = startRotation + deltaAngle;
      applyTransform(target, { rotation: newRotation });
    },
    [viewport]
  );

  /**
   * Handle corner resize (proportional scaling)
   * Uses distance-based scaling for a more natural Canva-like feel
   */
  const handleCornerResize = useCallback(
    (currentScreenPoint, startScreenPoint, handlePosition, target) => {
      const { startScale, startWidth, startHeight } = resizeState.current;

      // Convert both screen points to world space
      const canvas = viewport?.options?.events?.domElement || document.querySelector('canvas');
      const rect = canvas?.getBoundingClientRect();

      if (!rect) return;

      const startWorld = screenToWorld(
        viewport,
        startScreenPoint.x - rect.left,
        startScreenPoint.y - rect.top
      );
      const currentWorld = screenToWorld(
        viewport,
        currentScreenPoint.x - rect.left,
        currentScreenPoint.y - rect.top
      );

      // Calculate delta in world space
      const worldDeltaX = currentWorld.x - startWorld.x;
      const worldDeltaY = currentWorld.y - startWorld.y;

      const rotation = target.rotation || 0;

      // Rotate deltas into local space (relative to target's rotation)
      const cos = Math.cos(-rotation);
      const sin = Math.sin(-rotation);
      const localDeltaX = worldDeltaX * cos - worldDeltaY * sin;
      const localDeltaY = worldDeltaX * sin + worldDeltaY * cos;

      // Use the original element dimensions for more natural scaling
      const originalWidth = startWidth || target.width;
      const originalHeight = startHeight || target.height;

      // Calculate scale based on the ratio of movement to original size
      // This gives a more natural, Canva-like feel
      let scaleFactor = 0;

      switch (handlePosition) {
        case 'topLeft':
          scaleFactor = -(localDeltaX / originalWidth + localDeltaY / originalHeight);
          break;
        case 'topRight':
          scaleFactor = (localDeltaX / originalWidth - localDeltaY / originalHeight);
          break;
        case 'bottomLeft':
          scaleFactor = (-localDeltaX / originalWidth + localDeltaY / originalHeight);
          break;
        case 'bottomRight':
          scaleFactor = (localDeltaX / originalWidth + localDeltaY / originalHeight);
          break;
      }

      const newScale = Math.max(0.01, startScale * (1 + scaleFactor));

      applyTransform(target, { scaleX: newScale, scaleY: newScale });
    },
    [viewport]
  );

  /**
   * Handle side resize (non-proportional scaling)
   * Uses dimension-based scaling for natural feel
   */
  const handleSideResize = useCallback(
    (currentScreenPoint, startScreenPoint, handlePosition, target) => {
      const { startScale, startScaleY, startWidth, startHeight } = resizeState.current;

      // Convert both screen points to world space
      const canvas = viewport?.options?.events?.domElement || document.querySelector('canvas');
      const rect = canvas?.getBoundingClientRect();

      if (!rect) return;

      const startWorld = screenToWorld(
        viewport,
        startScreenPoint.x - rect.left,
        startScreenPoint.y - rect.top
      );
      const currentWorld = screenToWorld(
        viewport,
        currentScreenPoint.x - rect.left,
        currentScreenPoint.y - rect.top
      );

      // Calculate delta in world space
      const worldDeltaX = currentWorld.x - startWorld.x;
      const worldDeltaY = currentWorld.y - startWorld.y;

      const rotation = target.rotation || 0;

      // Rotate deltas into local space (relative to target's rotation)
      const cos = Math.cos(-rotation);
      const sin = Math.sin(-rotation);
      const localDeltaX = worldDeltaX * cos - worldDeltaY * sin;
      const localDeltaY = worldDeltaX * sin + worldDeltaY * cos;

      // Use the original element dimensions for more natural scaling
      const originalWidth = startWidth || target.width;
      const originalHeight = startHeight || target.height;

      let newScaleX = startScale;
      let newScaleY = startScaleY;

      switch (handlePosition) {
        case 'top':
          newScaleY = Math.max(0.01, startScaleY * (1 - (localDeltaY / originalHeight) * 2));
          break;
        case 'bottom':
          newScaleY = Math.max(0.01, startScaleY * (1 + (localDeltaY / originalHeight) * 2));
          break;
        case 'left':
          newScaleX = Math.max(0.01, startScale * (1 - (localDeltaX / originalWidth) * 2));
          break;
        case 'right':
          newScaleX = Math.max(0.01, startScale * (1 + (localDeltaX / originalWidth) * 2));
          break;
      }

      applyTransform(target, { scaleX: newScaleX, scaleY: newScaleY });
    },
    [viewport]
  );

  /**
   * Handle end of transform operation
   */
  const handleTransformEnd = useCallback(() => {
    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    if (!resizeState.current?.isResizing || !initialTransform.current) return;

    const { target, handleType } = resizeState.current;

    const finalTransform = createFinalTransform(target);

    // Only create undo command if transform actually changed
    const transformChanged = hasTransformChanged(
      initialTransform.current,
      finalTransform
    );

    if (transformChanged && transformElement) {
      transformElement(
        target.elementId,
        initialTransform.current,
        finalTransform
      );
    } else if (transformChanged) {
      updateElement(target.elementId, finalTransform);
    }

    // Clean up
    cleanupGlobalHandlers();
    resizeState.current = null;
    initialTransform.current = null;
  }, [transformElement, updateElement]);

  /**
   * Clean up global event handlers
   */
  const cleanupGlobalHandlers = useCallback(() => {
    if (globalHandlers.current.moveHandler) {
      document.removeEventListener(
        "mousemove",
        globalHandlers.current.moveHandler
      );
    }
    if (globalHandlers.current.upHandler) {
      document.removeEventListener("mouseup", globalHandlers.current.upHandler);
    }
  }, []);

  /**
   * Handle mouse down on transform handles
   */
  const handleHandleDown = useCallback(
    (event, handleType, handlePosition, container) => {
      event.stopPropagation();

      const target = container.target;

      // Store initial transform for undo/redo
      initialTransform.current = createInitialTransform(target);

      // Store the starting point in SCREEN coordinates
      const startPoint = {
        x: event.data.originalEvent.clientX,
        y: event.data.originalEvent.clientY,
      };

      // Create resize state with additional data for better scaling calculations
      const additionalData =
        handleType === "rotation"
          ? {
            startRotation: target.rotation,
          }
          : {
            startWidth: target.width,
            startHeight: target.height,
          };

      resizeState.current = createResizeState(
        target,
        container,
        startPoint,
        handleType,
        handlePosition,
        additionalData
      );

      // Add global event listeners
      document.addEventListener("mousemove", globalHandlers.current.moveHandler);
      document.addEventListener("mouseup", globalHandlers.current.upHandler);

      console.log(`Started ${handleType} transform:`, handlePosition);
    },
    []
  );

  // Store handlers in ref for access
  globalHandlers.current.moveHandler = handleGlobalMouseMove;
  globalHandlers.current.upHandler = handleTransformEnd;

  /**
   * Setup viewport events
   */
  const setupViewportEvents = useCallback(() => {
    if (!viewport || viewport._resizeEventsSetup) return;

    viewport.eventMode = "static";

    // Listen for zoom changes to update handle scales
    viewport.on("zoomed", () => {
      if (currentTransform.current?.update) {
        currentTransform.current.update();
      }
    });

    viewport._resizeEventsSetup = true;
  }, [viewport]);

  /**
   * Show transform controls for a target
   */
  const showTransformControls = useCallback(
    (target) => {
      hideTransformControls();
      setupViewportEvents();

      const controls = new TransformControls(viewport, target);
      controls.update();

      // Add event listeners
      controls.addEventListeners({
        onHandleDown: handleHandleDown,
      });

      if (viewport) {
        viewport.addChild(controls.getContainer());
        currentTransform.current = controls;
      }
    },
    [viewport, handleHandleDown, setupViewportEvents]
  );

  /**
   * Hide transform controls
   */
  const hideTransformControls = useCallback(() => {
    // Cancel any pending animation frame
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    if (currentTransform.current && viewport) {
      viewport.removeChild(currentTransform.current.getContainer());
      currentTransform.current.destroy();
      currentTransform.current = null;
    }

    // Clear any active resize state
    resizeState.current = null;
    cleanupGlobalHandlers();
  }, [viewport, cleanupGlobalHandlers]);

  /**
   * Update current transform controls
   */
  const updateCurrentTransform = useCallback(() => {
    if (currentTransform.current?.update) {
      currentTransform.current.update();
    }
  }, []);

  return {
    showTransformControls,
    hideTransformControls,
    updateCurrentTransform,
  };
};